/**
 * Frontend mirror of the FROZEN Live Match Events contract
 * (`docs/contracts/match-events-contract.md`). Kept in sync manually —
 * the frontend doesn't import from the backend workspace. If the
 * contract's `MatchEventFrame` / REST shapes change, update here too.
 *
 * The camelCase keys here MUST match the WS `match_event` body and the
 * REST payloads byte-for-byte.
 */

/** Category enum — the exact union from the contract (§`MatchEventFrame`). */
export type MatchEventCategory =
  // soccer
  | 'goal'
  | 'penalty'
  | 'corner'
  | 'card'
  | 'var'
  // baseball
  | 'run'
  | 'inning'
  | 'final'
  | 'home_run'
  | 'hit'
  | 'error'
  // tennis (final shared with baseball)
  | 'game'
  | 'set'
  | 'point'
  | 'serve'
  | 'ace'
  | 'double_fault'
  // shared fallback buckets
  | 'shot'
  | 'stat'
  | 'player_stat'
  | 'timer'
  | 'other';

/** Every category in display order — the full union across all sports. */
export const MATCH_EVENT_CATEGORIES: MatchEventCategory[] = [
  'goal',
  'penalty',
  'corner',
  'card',
  'var',
  'run',
  'inning',
  'final',
  'home_run',
  'hit',
  'error',
  'game',
  'set',
  'point',
  'serve',
  'ace',
  'double_fault',
  'shot',
  'stat',
  'player_stat',
  'timer',
  'other',
];

/**
 * Default-checked categories before a sport is known (soccer's set, for
 * back-compat with the URL-param reader). Sport-scoped filtering should use
 * {@link defaultCategoriesForSport}.
 */
export const DEFAULT_MATCH_EVENT_CATEGORIES: MatchEventCategory[] = [
  'goal',
  'penalty',
  'corner',
  'var',
  // The full-time (match-over) notification is high-signal in every sport, so
  // it is ON even before a sport resolves (the neutral fallback set).
  'final',
];

/** LSports `sport_id` header values the FE knows about. */
export const SPORT_IDS = {
  SOCCER: 6046,
  BASEBALL: 154914,
  TENNIS: 54094,
} as const;

/**
 * Per-sport filter vocabulary + default-on set — mirrors the contract's
 * Sport → category registry table. `categories` is the ordered list shown in
 * the filter for that sport; `defaults` is the initially-checked subset.
 */
export const SPORT_CATEGORY_VOCAB: Record<
  number,
  { categories: MatchEventCategory[]; defaults: MatchEventCategory[] }
> = {
  [SPORT_IDS.SOCCER]: {
    categories: [
      'goal',
      'penalty',
      'corner',
      'card',
      'var',
      'final',
      'shot',
      'stat',
      'player_stat',
      'timer',
      'other',
    ],
    defaults: ['goal', 'penalty', 'corner', 'var', 'final'],
  },
  [SPORT_IDS.BASEBALL]: {
    categories: [
      'run',
      'inning',
      'final',
      'home_run',
      'hit',
      'error',
      'stat',
      'player_stat',
      'timer',
      'other',
    ],
    defaults: ['run', 'inning', 'final', 'home_run'],
  },
  [SPORT_IDS.TENNIS]: {
    categories: [
      'game',
      'set',
      'final',
      'point',
      'serve',
      'ace',
      'double_fault',
      'stat',
      'player_stat',
      'timer',
      'other',
    ],
    defaults: ['game', 'set', 'final'],
  },
};

/**
 * Filter vocabulary for a sport. Unknown / null sport (no sport resolved yet)
 * → the full union, so nothing is hidden before a sport is known.
 */
export function categoriesForSport(
  sportId: number | null | undefined,
): MatchEventCategory[] {
  if (sportId == null) return [...MATCH_EVENT_CATEGORIES];
  return SPORT_CATEGORY_VOCAB[sportId]?.categories ?? [...MATCH_EVENT_CATEGORIES];
}

/**
 * Default-checked categories for a sport. Unknown / null sport → the soccer
 * defaults ({@link DEFAULT_MATCH_EVENT_CATEGORIES}), a safe neutral set.
 */
export function defaultCategoriesForSport(
  sportId: number | null | undefined,
): MatchEventCategory[] {
  if (sportId == null) return [...DEFAULT_MATCH_EVENT_CATEGORIES];
  return (
    SPORT_CATEGORY_VOCAB[sportId]?.defaults ?? [...DEFAULT_MATCH_EVENT_CATEGORIES]
  );
}

/** The `player` sub-object of a `MatchEventFrame`. */
export interface MatchEventPlayer {
  id: number;
  teamId: number;
  name: string;
}

/** The `period` sub-object of a `MatchEventFrame`. */
export interface MatchEventPeriod {
  id: number;
  name: string;
}

/**
 * `MatchEventFrame` — the `data` payload + WS `match_event` body. Exactly
 * the camelCase keys from the frozen contract.
 */
export interface MatchEventFrame {
  /** Redis stream id `<ms>-<seq>`; set by the relay, may be '' from the feed. */
  streamId: string;
  fixtureId: number;
  /** `Body.IncidentKey` — dedup / collapse key. */
  incidentKey: string;
  category: MatchEventCategory;
  /** AMQP header `sport_id` (6046 soccer, 154914 baseball, 54094 tennis); null if unresolved. */
  sportId: number | null;
  /** `Body.Id`. */
  incidentId: number | null;
  /** `Body.Name`. */
  name: string;
  /** `Body.Kind`. */
  kind: string;
  /** `"0:1"` | `"3"` | null — team stat → home:away; single → raw. */
  value: string | null;
  /** Position 1 (home). */
  homeValue: string | null;
  /** Position 2 (away). */
  awayValue: string | null;
  /** team-level side, or null when not trivially known. */
  team: 'home' | 'away' | null;
  /** first `PlayerList` entry, or null. */
  player: MatchEventPlayer | null;
  /** `Body.Seconds` (game clock). */
  seconds: number | null;
  /** `Body.Period`. */
  period: MatchEventPeriod | null;
  /** 0..1 (`Body.Confidence`, default 0). */
  confidence: number;
  /** ISO 8601 — feed receive time. */
  receivedAt: string;
  /** full `Body` verbatim — drives the "all fields" expand. */
  raw: Record<string, unknown>;
}

/**
 * A row in the `activatable_matches` list (REST `GET /match-events/matches`).
 * Mirrors the collapsed control-plane response — the schema fields plus the
 * runtime `active` flag (and any optional live-health read the backend adds).
 */
export interface ActivatableMatch {
  fixtureId: number;
  /** null / undefined = provisional (Auto) — resolved from the feed header. */
  sportId?: number | null;
  homeTeam: string;
  awayTeam: string;
  league?: string | null;
  startDate?: string | null;
  openerGroupId?: string | null;
  active: boolean;
  activatedAt?: string | null;
  deactivatedAt?: string | null;
  createdBy?: string | null;
  /** Optional live-health hint the backend may attach (Redis-derived). */
  live?: boolean;
}

/**
 * How an active match earned its slot in the live hub (REST
 * `GET /match-events/active`, frozen contract §REST):
 *   - `group`  — pulled in automatically by a sniper opener group.
 *   - `manual` — the operator registered + activated it by hand.
 *   - `both`   — registered manually AND covered by a group.
 */
export type MatchSource = 'group' | 'manual' | 'both';

/**
 * A row in the live-hub active-match list (REST `GET /match-events/active`,
 * frozen contract §REST). The match doc plus the runtime `source` provenance
 * and the `live` feed-health flag.
 */
export interface ActiveMatch {
  fixtureId: number;
  homeTeam: string;
  awayTeam: string;
  league?: string | null;
  /** null / undefined = provisional (Auto) — resolved from the feed header. */
  sportId?: number | null;
  startDate?: string | null;
  active: boolean;
  /** Owning sniper opener group, when the match came from a group. */
  sniperGroupId?: string | null;
  /** Provenance — group vs manual registration (or both). */
  source: MatchSource;
  /** Runtime feed health (Redis-derived): a live feed is attached. */
  live: boolean;
  /**
   * The fixture has ≥1 corner sniper market. When false the corner seed inputs
   * are hidden and the corner sound cue is muted (a goals-only match). Optional
   * for backward compat with an older control plane.
   */
  hasCornerMarkets?: boolean;
}

/**
 * A single sniper/manual operation on a match (WS `match_operation`, frozen
 * contract §WS). `kind` splits automatic (sniper) from manual (operator)
 * actions; `type` is the specific event (`sniper_fired`, `manual_order`, …).
 * All the order-shape fields are optional — a blocked fire carries a `reason`
 * but no fill, a manual order carries size/price but no `incidentKey`.
 *
 * Identity is the BACKEND's call, carried on `streamId` and assigned by
 * `resolveOpStreamId`. An op that re-emits (a fill series climbing 0 → 78 → …)
 * gets a NAMESPACED key beginning with `sniper:` — currently `sniper:<orderId>`
 * for the fires, `sniper:buyfill:<orderId>` / `sniper:sellfill:<orderId>` for
 * the fill series, and the buy-fill row is being re-keyed to be mark-scoped.
 * Every other op keeps the raw `<millis>-<seq>` entry id, which can never begin
 * with `sniper:`. The frontend matches the PREFIX and uses the key verbatim
 * (see `operationStableId`) rather than parsing it, so a namespace change does
 * not need a matching frontend change.
 *
 * Note which ops deliberately do NOT share a key: a placement and its fill are
 * separate namespaces, so a fill can never overwrite the placement row in place.
 */
export interface MatchOperation {
  fixtureId: number;
  kind: 'automatic' | 'manual';
  /** Specific op type: `sniper_fired` | `sniper_var_reversal` | `sniper_fire_blocked` | `manual_order` | … */
  type: string;
  /**
   * Stable op identity from the backend, present on BOTH the live and the Mongo
   * backfill paths — a `sniper:`-prefixed namespaced key when the op collapses,
   * a raw per-stream `<ms>-<seq>` entry id otherwise. A raw id is unique only
   * WITHIN its stream (the automatic and manual streams have independent id
   * sequences), so only the `sniper:` form is a usable identity.
   */
  streamId?: string | null;
  /** Deprecated alias, never sent by the backend; kept as a first-preference override. */
  id?: string | null;
  /**
   * The venue order id. LIVE ONLY — it is not on the `match_operations` schema
   * and not in the backfill frame, so it must never be used to derive row
   * identity (that made an order collapse differently live than after a
   * refresh). Informational only.
   */
  orderId?: string | null;
  venue?: string | null;
  /**
   * The VENUE ticker. NOT an identity — two sniper markets can point at the
   * same one — so never group or key by it. Display only.
   */
  marketId?: string | null;
  /**
   * The sniper MARK this op belongs to (`cfg.market_rule_id`), populated on
   * every op. This is the market identity: the buy-fill row is keyed by it
   * backend-side (`sniper:buyfill:<fixtureId>:<marketRuleId>`) because a mark's
   * gross is a mark-level number, and the venue `orderId` changes when the VAR
   * all-clear rebuy re-places the buy. Optional for back-compat with ops
   * emitted before the field existed.
   */
  marketRuleId?: string | null;
  marketLabel?: string | null;
  sizeRequested?: number | null;
  filled?: number | null;
  avgPrice?: number | null;
  side?: string | null;
  price?: number | null;
  /** The live incident that triggered the op (sniper ops), when applicable. */
  incidentKey?: string | null;
  reason?: string | null;
  /**
   * The triggering LSports frame's own confidence (0..1), present only when the
   * frame is what the op is ABOUT — today a goal RETRACTION (`sniper_goal_cancelled`).
   * A retraction at 10% and one at 100% call for opposite operator responses, so
   * the number is rendered next to the op rather than left in the backend log.
   */
  confidence?: number | null;
  /** ISO 8601 — op timestamp (feed-ordered together with incidents). */
  ts: string;
}

/** Body for `POST /match-events/matches` (upsert on `fixtureId`). */
export interface UpsertMatchDto {
  fixtureId: number;
  sportId?: number;
  homeTeam: string;
  awayTeam: string;
  league?: string | null;
  startDate?: string | null;
  openerGroupId?: string | null;
  createdBy?: string | null;
}

/**
 * The tab's filter state, persisted to URL params (`ev*` keys) so a
 * reload / bookmark restores the view. Mirrors the notifications page's
 * filter-in-URL pattern.
 */
export interface MatchEventsFilter {
  /** Selected categories (default: goal/penalty/corner/var). */
  categories: MatchEventCategory[];
  /** Minimum confidence 0..1 (default 0). */
  minConfidence: number;
  /** Show every update (else collapse by incidentKey, keep best). */
  showAll: boolean;
  /** Request the raw firehose (`includeRaw` on the subscription). */
  raw: boolean;
  /** Currently-selected fixture, or null. */
  fixtureId: number | null;
  /** Operation-row filter: automatic (sniper) / manual / all. */
  opMode: OpFilterMode;
}

/** Operation-row filter mode for the live hub. */
export type OpFilterMode = 'auto' | 'manual' | 'all';
